/**
 * Crocin Labs PDP V2 — enhanced interactions.
 * Handles: gallery thumbnails, purchase type toggle (subscribe-first),
 * frequency picker, quantity, price updates.
 * STAGING ONLY — merge with /assets/crocin-pdp.js when ready.
 */

(function () {
  'use strict';

  // --- Ingredient carousel — prev/next scroll (independent of the buy box) ---
  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-crocin-carousel]').forEach(function (carousel) {
      if (carousel.dataset.crocinCarouselReady === '1') return;
      var track = carousel.querySelector('[data-crocin-carousel-track]');
      var prevBtn = carousel.querySelector('[data-crocin-carousel-prev]');
      var nextBtn = carousel.querySelector('[data-crocin-carousel-next]');
      if (!track || !prevBtn || !nextBtn) return;
      carousel.dataset.crocinCarouselReady = '1';

      function step() {
        var card = track.querySelector('.crocin-pdp-ingredients__card');
        return card ? card.offsetWidth + 20 : 300;
      }
      function updateArrows() {
        prevBtn.disabled = track.scrollLeft <= 4;
        nextBtn.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      }
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    });
  }
  initCarousels();
  document.addEventListener('shopify:section:load', function (e) {
    initCarousels(e.target);
  });

  const root = document.querySelector('.crocin-pdp');
  if (!root) return;

  const buyBox = root.querySelector('[data-crocin-buy-box]');
  if (!buyBox) return;

  const galleryMain = root.querySelector('[data-crocin-gallery-main]');
  const thumbs = root.querySelectorAll('.crocin-pdp__gallery-thumb');
  const purchaseOptions = buyBox.querySelectorAll('[data-crocin-purchase-option]');
  const frequencyOptions = buyBox.querySelectorAll('[data-crocin-frequency-option] input');
  const sellingPlanInput = buyBox.querySelector('[data-crocin-selling-plan-input]');
  const priceEl = buyBox.querySelector('.crocin-pdp__price-current');
  const compareEl = buyBox.querySelector('[data-crocin-price-compare]');
  const saveEl = buyBox.querySelector('[data-crocin-price-save]');
  const subPriceEl = buyBox.querySelector('[data-crocin-sub-price]');
  const saveTagEl = buyBox.querySelector('[data-crocin-save-tag]');
  const submitBtn = buyBox.querySelector('[data-crocin-submit]');
  const qtyField = buyBox.querySelector('[data-crocin-qty-field]');
  const qtyMinus = buyBox.querySelector('[data-crocin-qty-minus]');
  const qtyPlus = buyBox.querySelector('[data-crocin-qty-plus]');

  const onetimePrice = parseInt(buyBox.dataset.crocinOnetimePrice || '0', 10);

  // --- Gallery ---
  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const img = thumb.querySelector('img');
      if (!img || !galleryMain) return;

      const srcset = img.getAttribute('srcset') || '';
      const mainImg = galleryMain.querySelector('img');
      if (mainImg && srcset) {
        mainImg.setAttribute('srcset', srcset.replace(/100w|200w/g, (m) =>
          m === '100w' ? '400w' : '1200w'
        ));
        mainImg.setAttribute('src', img.src.replace(/width=\d+/, 'width=1200'));
      }

      thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    });
  });

  // --- Purchase type toggle ---
  purchaseOptions.forEach((option) => {
    const radio = option.querySelector('input[type="radio"]');
    if (!radio) return;

    radio.addEventListener('change', () => {
      purchaseOptions.forEach((o) => o.classList.remove('crocin-pdp__purchase-option--active'));
      option.classList.add('crocin-pdp__purchase-option--active');

      if (radio.value === 'subscribe') {
        const firstFreq = buyBox.querySelector('[data-crocin-frequency-option] input:checked');
        if (firstFreq) {
          sellingPlanInput.value = firstFreq.value;
          updateSubscribePrice(firstFreq);
        }
      } else {
        sellingPlanInput.value = '';
        updateOnetimePrice();
      }
    });
  });

  // --- Frequency picker ---
  frequencyOptions.forEach((input) => {
    input.addEventListener('change', () => {
      sellingPlanInput.value = input.value;
      updateSubscribePrice(input);

      buyBox.querySelectorAll('[data-crocin-frequency-option]').forEach((label) => {
        label.setAttribute('aria-pressed', label.contains(input) ? 'true' : 'false');
      });
    });
  });

  function updateSubscribePrice(freqInput) {
    const discountPct = parseInt(freqInput.dataset.crocinDiscountPct || '0', 10);
    if (discountPct > 0 && onetimePrice > 0) {
      const subPrice = Math.round(onetimePrice * (100 - discountPct) / 100);
      if (priceEl) priceEl.textContent = formatMoney(subPrice);
      if (compareEl) {
        compareEl.textContent = formatMoney(onetimePrice);
        compareEl.hidden = false;
      }
      if (saveEl) {
        saveEl.textContent = 'Save ' + discountPct + '%';
        saveEl.hidden = false;
      }
      if (subPriceEl) subPriceEl.textContent = formatMoney(subPrice);
      if (saveTagEl) saveTagEl.textContent = 'Save ' + discountPct + '%';
    }
  }

  function updateOnetimePrice() {
    if (priceEl) priceEl.textContent = formatMoney(onetimePrice);
    if (compareEl) compareEl.hidden = true;
    if (saveEl) saveEl.hidden = true;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // --- Quantity ---
  if (qtyMinus && qtyPlus && qtyField) {
    qtyMinus.addEventListener('click', () => {
      const val = parseInt(qtyField.value, 10) || 1;
      if (val > 1) qtyField.value = val - 1;
    });
    qtyPlus.addEventListener('click', () => {
      const val = parseInt(qtyField.value, 10) || 1;
      qtyField.value = val + 1;
    });
  }

  // --- Variant picker ---
  const variantOptions = buyBox.querySelectorAll('[data-crocin-variant-option]');
  variantOptions.forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('.crocin-pdp__variant-options');
      if (group) {
        group.querySelectorAll('[data-crocin-variant-option]').forEach((b) =>
          b.setAttribute('aria-pressed', 'false')
        );
      }
      btn.setAttribute('aria-pressed', 'true');
    });
  });

  // --- Initialize: if subscribe is checked by default, set selling plan ---
  const checkedSubscribe = buyBox.querySelector('[data-crocin-purchase-option="subscribe"] input:checked');
  if (checkedSubscribe) {
    const firstFreq = buyBox.querySelector('[data-crocin-frequency-option] input:checked');
    if (firstFreq) {
      sellingPlanInput.value = firstFreq.value;
      updateSubscribePrice(firstFreq);
    }
  }
})();
